// Форма входа (задача D4): почта и пароль Омнидеска. Одна на экран входа и окно «Сессия истекла».
//
// По ux-engine:form-ux:
// - подписи над полями, автозаполнение (`username`, `current-password`), у пароля — «Показать пароль»;
// - значения читаются из формы при отправке (FormData): так их видит и менеджер паролей, заполнивший поля сам;
// - проверка — при отправке, тем же правилом почты, что у сервера (`LoginBodySchema`); подсказка — под полем,
//   фокус — на первое поле с ошибкой; ошибка входа — строкой в role="alert" над кнопкой;
// - «Войти» во время входа: спиннер 14 px поверх, подпись и ширина не меняются, повторная отправка (щелчок, Enter)
//   ничего не делает, фокус остаётся на кнопке.
import { LoginBodySchema, type Me } from '@contract/index.js';
import { cn } from 'cn';
import { CircleAlert, Eye, EyeOff, LoaderCircle } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ApiError, messageRuOf, ServiceUnavailableError } from '@/api/errors';
import { useLogin } from '@/api/queries/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const LOGIN_TEXTS_RU = {
  email: 'Почта',
  password: 'Пароль',
  submit: 'Войти',
  showPassword: 'Показать пароль',
  emailMissing: 'Введите почту — ту же, что в Омнидеске.',
  emailInvalid: 'Похоже, в почте опечатка — проверьте её.',
  passwordMissing: 'Введите пароль.',
  serviceDown: 'Сервис Анны не отвечает: войти сейчас нельзя. Попробуйте через минуту.',
} as const;

interface FieldErrors {
  email?: string;
  password?: string;
}

/** Значение поля формы строкой (у файловых полей — пусто). */
function fieldValue(data: FormData, name: string): string {
  const value = data.get(name);
  return typeof value === 'string' ? value : '';
}

function validate(email: string, password: string): FieldErrors {
  let emailError: string | undefined;
  if (email === '') emailError = LOGIN_TEXTS_RU.emailMissing;
  else if (!LoginBodySchema.shape.email.safeParse(email).success) emailError = LOGIN_TEXTS_RU.emailInvalid;
  return {
    ...(emailError === undefined ? {} : { email: emailError }),
    ...(password === '' ? { password: LOGIN_TEXTS_RU.passwordMissing } : {}),
  };
}

/** Текст ошибки входа: сервис не ответил — свой текст, ошибка сервера — его текст. */
function loginErrorRu(error: unknown): string {
  if (error instanceof ServiceUnavailableError) return LOGIN_TEXTS_RU.serviceDown;
  return messageRuOf(error);
}

export interface LoginFormProps {
  /** Вход прошёл: `me` уже в кэше, окно «Сессия истекла» закрыто. */
  readonly onSuccess?: (me: Me) => void;
  /** `page` — кнопка «Войти» во всю ширину; `dialog` — кнопки справа, рядом — `secondaryAction`. */
  readonly layout: 'page' | 'dialog';
  /** Вторая кнопка в окне («Выйти»). */
  readonly secondaryAction?: ReactNode;
  /** Поставить фокус в «Почту» при открытии (экран входа; в окне фокус ставит само окно). */
  readonly focusOnMount?: boolean;
  readonly className?: string;
}

export function LoginForm({ onSuccess, layout, secondaryAction, focusOnMount = false, className }: LoginFormProps) {
  const login = useLogin();
  const id = useId();
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const pending = login.isPending;

  useEffect(() => {
    if (focusOnMount) emailRef.current?.focus();
  }, [focusOnMount]);

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (pending) return;
    const data = new FormData(event.currentTarget);
    const email = fieldValue(data, 'email').trim();
    const password = fieldValue(data, 'password');
    const errors = validate(email, password);
    setFieldErrors(errors);
    setFormError(null);
    if (errors.email !== undefined) {
      emailRef.current?.focus();
      return;
    }
    if (errors.password !== undefined) {
      passwordRef.current?.focus();
      return;
    }
    login.mutate(
      { email, password },
      {
        onSuccess: (me) => onSuccess?.(me),
        onError: (error) => {
          setFormError(loginErrorRu(error));
          if (error instanceof ApiError && error.code === 'bad_credentials') {
            passwordRef.current?.focus();
            passwordRef.current?.select();
          }
        },
      },
    );
  }

  function clearFieldError(field: keyof FieldErrors): void {
    if (fieldErrors[field] === undefined) return;
    setFieldErrors((previous) => {
      const next = { ...previous };
      delete next[field];
      return next;
    });
  }

  const submit = (
    <Button
      type="submit"
      size="lg"
      disabled={pending}
      focusableWhenDisabled
      aria-busy={pending || undefined}
      className={cn('data-disabled:opacity-100', layout === 'page' && 'w-full')}
    >
      <span className="relative inline-flex items-center">
        {pending && (
          <LoaderCircle data-slot="spinner" aria-hidden="true" className="absolute -left-5 size-3.5 animate-spin" />
        )}
        {LOGIN_TEXTS_RU.submit}
      </span>
    </Button>
  );

  const emailId = `${id}-email`;
  const passwordId = `${id}-password`;

  return (
    <form noValidate onSubmit={onSubmit} className={cn('flex flex-col gap-4', className)}>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={emailId}>{LOGIN_TEXTS_RU.email}</Label>
        <Input
          ref={emailRef}
          id={emailId}
          name="email"
          type="email"
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-invalid={fieldErrors.email === undefined ? undefined : true}
          aria-describedby={fieldErrors.email === undefined ? undefined : `${emailId}-error`}
          onChange={() => clearFieldError('email')}
        />
        {fieldErrors.email !== undefined && (
          <p id={`${emailId}-error`} className="text-sm text-destructive">
            {fieldErrors.email}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={passwordId}>{LOGIN_TEXTS_RU.password}</Label>
        <div className="relative">
          <Input
            ref={passwordRef}
            id={passwordId}
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            className="pr-9 max-sm:pr-11"
            aria-invalid={fieldErrors.password === undefined ? undefined : true}
            aria-describedby={fieldErrors.password === undefined ? undefined : `${passwordId}-error`}
            onChange={() => clearFieldError('password')}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={LOGIN_TEXTS_RU.showPassword}
            aria-pressed={showPassword}
            aria-controls={passwordId}
            className="absolute top-1/2 right-0.5 -translate-y-1/2 text-muted-foreground max-sm:right-0 max-sm:size-10"
            onClick={() => setShowPassword((shown) => !shown)}
          >
            {showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
          </Button>
        </div>
        {fieldErrors.password !== undefined && (
          <p id={`${passwordId}-error`} className="text-sm text-destructive">
            {fieldErrors.password}
          </p>
        )}
      </div>

      {formError !== null && (
        <div role="alert" className="flex items-start gap-2 rounded-md bg-st-error-soft px-3 py-2 text-st-error">
          <CircleAlert aria-hidden="true" className="mt-px size-4 shrink-0" />
          <p>{formError}</p>
        </div>
      )}

      {layout === 'page' ? (
        submit
      ) : (
        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          {secondaryAction}
          {submit}
        </div>
      )}
    </form>
  );
}
